import React, { useEffect, useRef } from "react";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  /** Styles the action as destructive and colours it with the record accent. */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A confirmation built on the native `<dialog>` element.
 *
 * `showModal()` brings focus trapping, Escape-to-close, inertness of the page
 * behind, and the top layer — all things a hand-rolled overlay has to
 * reimplement and usually gets wrong.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  body,
  confirmLabel,
  destructive,
  busy,
  onConfirm,
  onCancel,
}) => {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      // Escape fires `cancel`; route it through the same handler so the parent
      // state cannot drift out of sync with the element.
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
      onClose={() => {
        if (open && !busy) onCancel();
      }}
      // `m-auto` restores the centring the UA stylesheet gives a modal dialog;
      // Tailwind's preflight resets margin to 0 and parks it in the corner.
      className="m-auto w-[420px] max-w-[90vw] rounded-[14px] border border-[var(--edge-strong)] bg-bay-850 p-0 text-ink-100 shadow-2xl backdrop:bg-black/60 backdrop:backdrop-blur-[2px]"
    >
      <div className="flex flex-col gap-3 p-5">
        <h2 className="text-[15px] font-semibold text-ink-100">{title}</h2>
        <div className="text-[13px] leading-relaxed text-ink-300">{body}</div>

        <div className="mt-2 flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={destructive ? "btn btn-danger" : "btn btn-primary"}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
};
